import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

interface Props {
	fragmento: string;
	/** Plantel del cliente: en Monterrey y Torreón se dice "inscripción" (Esteban, 2026-10-20). */
	plantelId?: number;
	onCerrar: () => void;
}

const DICEN_INSCRIPCION = [1, 2];   // MTY, TRC

/*
	Sonido de error al abrir el aviso (Esteban, 2026-10-20). WebAudio, sin archivo. El iPhone solo deja sonar un AudioContext
	que se prendió con un toque: prepararSonido() va en el clic de Enviar, y el aviso suena cuando llega la respuesta.
*/
let audio: AudioContext | null = null;
export const prepararSonido = () => {
	try {
		const Context = (window as any).AudioContext || (window as any).webkitAudioContext;
		if (!Context) return;
		if (!audio) audio = new Context();
		if (audio && audio.state === "suspended") audio.resume().catch(() => undefined);
	} catch { /* sin audio */ }
};
/** Zumbido de error: dos tonos graves que bajan. */
const sonarError = () => {
	try {
		prepararSonido();
		if (!audio) return;
		const t0 = audio.currentTime + 0.02;
		[[300, 0], [190, 0.22]].forEach(([hz, desde]) => {
			const osc = audio!.createOscillator();
			const vol = audio!.createGain();
			osc.type = "square";
			osc.frequency.value = hz;
			vol.gain.setValueAtTime(0.0001, t0 + desde);
			vol.gain.exponentialRampToValueAtTime(0.18, t0 + desde + 0.02);
			vol.gain.setValueAtTime(0.18, t0 + desde + 0.16);
			vol.gain.exponentialRampToValueAtTime(0.0001, t0 + desde + 0.2);
			osc.connect(vol);
			vol.connect(audio!.destination);
			osc.start(t0 + desde);
			osc.stop(t0 + desde + 0.21);
		});
	} catch { /* sin audio */ }
};

/** Segundos antes de poder cerrar: que lo lean, no que lo cierren de reflejo. */
const ESPERA = 3;

/**
 * La matrícula es gratis (Esteban, 2026-10-20). El mensaje que le puso costo ya salió (el detector puede equivocarse y no se
 * frena a nadie), pero la asesora ve este aviso rojo y lo tiene que cerrar con el botón: ni tocando fuera ni con Esc.
 */
const AvisoMatriculaGratis: React.FC<Props> = ({ fragmento, plantelId, onCerrar }) => {
	const palabra = DICEN_INSCRIPCION.includes(plantelId || 0) ? "inscripción" : "matrícula";
	const [falta, setFalta] = useState<number>(ESPERA);
	// Una sola vez por aviso (en desarrollo React monta dos veces; el ref sobrevive a ese segundo montaje).
	const sono = useRef<boolean>(false);
	useEffect(() => { if (!sono.current) { sono.current = true; sonarError(); } }, []);
	useEffect(() => {
		if (falta <= 0) return;
		const t = window.setTimeout(() => setFalta(falta - 1), 1000);
		return () => window.clearTimeout(t);
	}, [falta]);
	return (
		<Modal show onHide={() => undefined} backdrop="static" keyboard={false} centered className="matgratis-modal"
			backdropClassName="matgratis-fondo" aria-labelledby="matgratis-titulo">
			<div className="matgratis-banda">
				<svg viewBox="0 0 24 24" width="56" height="56" aria-hidden="true"><path fill="currentColor" d="M12 2 1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V9h2v4z" /></svg>
				<h2 id="matgratis-titulo">¡Dato Incorrecto!</h2>
			</div>
			<Modal.Body>
				<p className="matgratis-grande">La {palabra} es <b>GRATIS</b>.</p>
				<p className="matgratis-linea">Le dijiste a un cliente que la {palabra} tiene costo. ¡Por favor solo decir que es gratis!</p>
				{fragmento ? (
					<div className="matgratis-frag">
						<span>Lo que escribiste</span>
						<q>…{fragmento}…</q>
					</div>
				) : null}
			</Modal.Body>
			<Modal.Footer>
				<button className="matgratis-ok" disabled={falta > 0} autoFocus onClick={onCerrar}>
					{falta > 0 ? "Entendido (" + falta + ")" : "Entendido"}
				</button>
			</Modal.Footer>
		</Modal>
	);
};

export default AvisoMatriculaGratis;
